import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserPlus, Mail, Phone, FileSignature, User, Cake, MapPin, IdCard, CreditCard, Percent, StickyNote } from "lucide-react";
import { fetchArtists, createArtist } from "../lib/driveClient";
import ContactDetailModal from "../components/ContactDetailModal";
import { EntityFormModal, EntityField } from "../components/EntityFormModal";

const empty = {
  first_name: "",
  last_name: "",
  artist_name: "",
  email: "",
  phone: "",
  address: "",
  zip_city: "",
  birthdate: "",
  tax_id: "",
  iban: "",
  bic: "",
  bank_name: "",
  commission_percent: 40,
  notes: "",
};

export default function DriveArtists({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [openContact, setOpenContact] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchArtists();
      setRows(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.artist_name) {
      toast.error("Künstlername ist Pflicht");
      return;
    }
    setBusy(true);
    try {
      await createArtist({
        ...form,
        birthdate: form.birthdate || null,
        commission_percent: Number(form.commission_percent) || 40,
      });
      toast.success("Künstler angelegt");
      setForm(empty);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex items-start justify-between flex-wrap gap-4">
        <div>
          {!embedded && (
            <>
              <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
                <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
                Künstler
              </div>
              <h1 className="font-display font-black uppercase tracking-tighter leading-none text-3xl md:text-4xl">
                Roster verwalten
              </h1>
            </>
          )}
          {embedded && (
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
              {rows.length} {rows.length === 1 ? "Künstler" : "Künstler"}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="btn-new-artist"
          className="inline-flex items-center gap-2 px-5 py-3 text-[10px] font-bold tracking-[0.25em] uppercase bg-white text-black hover:bg-[#ff0033] hover:text-white transition-colors"
        >
          <UserPlus size={13} /> Neuer Künstler
        </button>
      </motion.div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-500">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-10 text-center text-sm text-zinc-500">
          Noch keine Künstler. Lege den ersten oben an.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((a) => (
            <div
              key={a.id}
              data-testid={`artist-${a.id}`}
              onClick={() => window.location.assign(`/drive/artists/${a.id}`)}
              className="bg-[#0A0A0A] border border-white/[0.06] p-5 hover:border-[#ff0033]/30 transition-colors cursor-pointer group"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-2 flex items-center justify-between">
                <span>Künstler</span>
                <span className="text-zinc-600 group-hover:text-zinc-400 tracking-normal">Öffnen →</span>
              </div>
              <div className="font-display font-black uppercase text-xl tracking-tight mb-1 truncate">{a.artist_name}</div>
              {(a.first_name || a.last_name) && (
                <div className="text-xs text-zinc-500 truncate">
                  {a.first_name} {a.last_name}
                </div>
              )}
              <div className="mt-4 space-y-1.5 text-xs text-zinc-400">
                {a.email && (
                  <div className="flex items-center gap-2 truncate">
                    <Mail size={11} className="text-zinc-600 shrink-0" /> {a.email}
                  </div>
                )}
                {a.phone && (
                  <div className="flex items-center gap-2 truncate">
                    <Phone size={11} className="text-zinc-600 shrink-0" /> {a.phone}
                  </div>
                )}
              </div>
              <Link
                to="/drive/contracts"
                onClick={(e) => e.stopPropagation()}
                data-testid={`artist-contract-${a.id}`}
                className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold text-[#ff0033] hover:text-white border-t border-white/[0.06] pt-3 w-full"
              >
                <FileSignature size={11} /> Vertrag erstellen →
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <EntityFormModal
          title="Neuen Künstler anlegen"
          onClose={() => setShowForm(false)}
          onSubmit={submit}
          busy={busy}
          submitLabel="Künstler anlegen"
        >
          <EntityField icon={<User size={13} />} label="Künstlername" required full value={form.artist_name} onChange={upd("artist_name")} placeholder="Bühnenname" testid="artist-name" />
          <EntityField icon={<User size={13} />} label="Vorname" value={form.first_name} onChange={upd("first_name")} testid="artist-firstname" />
          <EntityField icon={<User size={13} />} label="Nachname" value={form.last_name} onChange={upd("last_name")} testid="artist-lastname" />
          <EntityField icon={<Cake size={13} />} label="Geburtsdatum" type="date" value={form.birthdate} onChange={upd("birthdate")} testid="artist-birthdate" />
          <EntityField icon={<Mail size={13} />} label="E-Mail" type="email" value={form.email} onChange={upd("email")} testid="artist-email" />
          <EntityField icon={<Phone size={13} />} label="Telefon" value={form.phone} onChange={upd("phone")} testid="artist-phone" />
          <EntityField icon={<MapPin size={13} />} label="Adresse" value={form.address} onChange={upd("address")} placeholder="Straße" testid="artist-address" />
          <EntityField icon={<MapPin size={13} />} label="PLZ / Ort" value={form.zip_city} onChange={upd("zip_city")} testid="artist-zipcity" />
          <EntityField icon={<IdCard size={13} />} label="Steuer-ID" value={form.tax_id} onChange={upd("tax_id")} testid="artist-taxid" />
          <EntityField icon={<Percent size={13} />} label="Label-Anteil (%)" type="number" step="0.5" value={form.commission_percent} onChange={upd("commission_percent")} testid="artist-commission" />
          <EntityField icon={<CreditCard size={13} />} label="IBAN" value={form.iban} onChange={upd("iban")} testid="artist-iban" />
          <EntityField icon={<CreditCard size={13} />} label="BIC" value={form.bic} onChange={upd("bic")} testid="artist-bic" />
          <EntityField icon={<CreditCard size={13} />} label="Bank" full value={form.bank_name} onChange={upd("bank_name")} testid="artist-bank" />
          <EntityField icon={<StickyNote size={13} />} label="Notizen" full rows={3} value={form.notes} onChange={upd("notes")} testid="artist-notes" />
        </EntityFormModal>
      )}

      {/* Detail modal */}
      {openContact && (
        <ContactDetailModal
          contact={openContact}
          type="artist"
          onClose={() => setOpenContact(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
